import React, { useMemo, useState } from "react";
import { useData } from "../../../../context/DataContext.jsx";
import { openUnlessCancelled } from "../../../../lib/quotePersistence.js";
import { C, btnS, lbl } from "../../../../lib/styles.js";
import { fl, fm, fmt, fmtDate, FMT_DATE_FULL } from "../../../../lib/utils.js";

// fmtDate parses YYYY-MM-DD only (Number("28T12…") = NaN → "Invalid Date"), but
// version timestamps and createdAt are full ISO — feed it the day, on the
// viewer's own calendar (eventDay), not the UTC day.
const fmtIsoDay = (v) => fmtDate(eventDay(v), FMT_DATE_FULL);
import { DOC_TYPES } from "../../../../lib/constants.js";
import { calcLineDiscount, calcTotals, resolveQuoteRevenue, valueProvided } from "../../../../lib/builderHelpers.js";
import { isProBono, setProBono } from "../../../../lib/proBono.js";
import { jobRevenueParts, jobRevenueSyncLocked } from "../../lib/revenue.js";
import { amendmentVersions, currentVersionSince, eventDay } from "../../../../lib/quoteVersions.js";
import QuotePdfPreview from "../QuotePdfPreview.jsx";
import notify from "../../../../lib/notify.jsx";

/*
  FinancialsTab — ported from legacy nyc-suite (JobsPanel.jsx financials tab).
  Admin-only by visibleTabs; shows the money side of a job: current total from
  the linked quote, line items, Invoiced/Paid milestones, quote version
  history, related change-order quotes, and the amend / change-order actions.

  Departure from legacy: PDF download opens the QuotePdfPreview overlay (the
  builder's export path — addendum images resolved to base64, pricing sheet
  included per quote.includePricingSheet) instead of the legacy
  downloadQuotePdf variant system, which suite-io never had.
*/
export default function FinancialsTab({ job, linkedQuote, editing, onOpenBuilder, onOpenVersions }) {
  const { savedQuotes, jobs, toggleJobMilestone, updateJob, amendQuote, createChangeOrderQuote, loadQuote, duplicateQuote, unsavedEditsNotice } = useData();

  const [confirmUnInvoice, setConfirmUnInvoice] = useState(false);
  const [confirmAmend, setConfirmAmend] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [showPdf, setShowPdf] = useState(false);

  const docType = linkedQuote?.docType || "estimate";
  const docLabelShort = docType === "quote" ? "Quote" : docType === "rate_sheet" ? "Rate Sheet" : docType === "change_order" ? "Change Order" : "Estimate";
  const lineItems = linkedQuote?.lineItems || job.lineItems || [];
  // Pro bono (2026-09-11): the JOB's billing flag is what this card answers to.
  // The discounts shown come from the quote when one is linked (same rule as the
  // lines); when the job's flag disagrees with the quote's, the Pro bono line is
  // added or removed here for display through the same helper the flip writes
  // with, so Subtotal / Pro bono / $0 always reads true for THIS job.
  const proBono = isProBono(job);
  const baseDiscounts = linkedQuote?.quoteDiscounts || job.quoteDiscounts || [];
  const quoteDiscounts = proBono === isProBono(linkedQuote || job)
    ? baseDiscounts
    : setProBono({ quoteDiscounts: baseDiscounts }, proBono).quoteDiscounts;
  // What the client would otherwise have paid: line rates net of per-line
  // discounts, before the Pro bono discount. Line rates are never zeroed.
  const givenValue = valueProvided(lineItems);
  // Flip the job between standard and pro bono. Writes the flag, the job's own
  // discount list (the 100% Pro bono line added or removed), and the revenue the
  // money surfaces read: $0 while pro bono, the discounted total once standard
  // again. The linked quote is NOT rewritten here; see the handback note.
  const flipProBono = (on) => {
    const patch = setProBono({ quoteDiscounts: baseDiscounts }, on);
    patch.grossRevenue = on ? 0 : calcTotals(lineItems, patch.quoteDiscounts).total;
    updateJob?.(job.id, patch);
  };
  // The audited chokepoint (builderHelpers) computes the money the table shows,
  // so a discounted quote reconciles with its own totals (JM9).
  const totals = calcTotals(lineItems, quoteDiscounts);
  // Rolled-up project total (2026-08-25 model): the job's recorded revenue plus
  // its change orders — booked COs at their child job's figure, open CO quotes
  // at their own total. Same parts builder Insights and the lists use.
  const parts = jobRevenueParts(job, savedQuotes, jobs);
  const coCount = parts.changeOrders.length;
  // The quote's CURRENT worth vs the revenue frozen on the job (JM8): the sync
  // guard skips any job with a billing marker (invoiced/paid OR a lingering
  // invoiceDate/paidDate — un-invoicing keeps the date, gpt F4), so this
  // divergence used to hide behind a transient toast. Mirror the guard's exact
  // predicate and surface it persistently instead.
  const recordedRevenue = Number(job.grossRevenue) || 0;
  const quoteNowTotal = linkedQuote ? resolveQuoteRevenue(linkedQuote) : null;
  // A pro bono job records $0 by rule, so its quote's figure is not a divergence.
  const revenueMismatch = !!linkedQuote && !isProBono(job) && jobRevenueSyncLocked(job) && job.grossRevenue != null
    && Math.abs(recordedRevenue - quoteNowTotal) > 0.005;
  const quoteTotal = linkedQuote?.total ?? job.grossRevenue ?? 0;
  const versions = linkedQuote?.versions || [];
  const visibleVersions = versions.filter(v => !v?.markerOnly && v?.source !== "post-restore-marker");
  const hasRestorableVersion = versions.some(v => v?.schemaVersion === 2 && !v?.markerOnly && v?.source !== "post-restore-marker");
  const isAmended = amendmentVersions(visibleVersions).length > 0;
  // Version dates (2026-09-13): the current row says when this version came
  // into effect (the last amend or restore event), the archived rows say when
  // they were archived, and both are labelled so the column never implies an
  // order it does not have. The quote date is only the fallback for a quote
  // with no history at all.
  const currentSince = currentVersionSince(linkedQuote) || linkedQuote?.quoteDate || job.quoteDate || job.createdAt;
  const isInvoiced = !!job.invoiced;
  const isPaid = !!job.paid;
  const relatedQuotes = useMemo(
    () => (savedQuotes || []).filter(qq => qq.changeOrderOfJobId === job.id && !qq.deletedAt),
    [savedQuotes, job.id]
  );
  const invoicedColor = "#7AC6B8";
  const paidColor = "#7CA982";

  return (<div>
    {/* Total banner — the rolled-up project figure (job + change orders).
        Pro bono: the figure is the value given, labelled so, with the badge. */}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: C.surfaceAlt, borderRadius: 6, marginBottom: 16 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.08em", color: C.muted }}>{proBono ? "Value provided" : "Current Total"}</div>
          {proBono && <ProBonoBadge />}
        </div>
        <div style={{ fontSize: 10, fontWeight: 400, color: C.muted, marginTop: 2 }}>{linkedQuote
          // Value provided is this job's own lines; the change-order clause belongs
          // to the rolled-up total, so it is not claimed on a pro bono banner.
          ? `${docLabelShort}${isAmended ? " · Amended" : ""}${!proBono && coCount > 0 ? ` · incl. ${coCount} change order${coCount > 1 ? "s" : ""}` : ""}${proBono ? " · billed $0" : ""}`
          : proBono ? "No quote linked to this job · billed $0" : "No quote linked to this job"}</div>
      </div>
      <div data-testid="jfin-banner-figure" style={{ fontSize: 20, fontWeight: 400, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmt(proBono ? givenValue : parts.total)}</div>
    </div>

    {/* Persistent divergence flag (JM8): the quote changed after invoicing and
        the revenue sync was skipped — say so until it's resolved, not in a toast. */}
    {revenueMismatch && (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 6, marginBottom: 16, fontSize: 11, color: C.text }}>
        <span style={{ color: C.amber, fontSize: 14, flexShrink: 0 }}>{"⚠"}</span>
        <span>{`The ${docLabelShort.toLowerCase()}'s current total ${fmt(quoteNowTotal)} no longer matches the recorded revenue ${fmt(recordedRevenue)} — billing was already recorded on this job, so revenue updates are locked. Use Version History to review or force an update.`}</span>
      </div>
    )}

    {/* Line Items — gross per row, savings noted, totals via calcTotals so the
        table reconciles with the banner on discounted quotes (JM9) */}
    {lineItems.length > 0 && (<>
      <div style={lbl}>Line Items</div>
      <table className="jfin-litable" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          {["Description", "Qty", "Rate", "Total"].map((h, i) => (
            <th key={h} style={{ textAlign: i === 3 ? "right" : "left", fontSize: 9.5, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.1em", color: C.muted, padding: "6px 0", borderBottom: `1px solid ${C.border}`, background: "#FAFAF8" }}>{h}</th>
          ))}
        </tr></thead>
        <tbody>{lineItems.map((li, i) => {
          const hasQty = li.qty != null && li.qty > 0;
          const liTotal = (hasQty ? li.qty : 0) * (Number(li.rate) || 0);
          const lineDisc = calcLineDiscount(li);
          const isNeg = liTotal < 0 || (Number(li.rate) || 0) < 0;
          return (<tr key={i}>
            <td data-label="Description" style={{ fontSize: 12, fontWeight: 300, padding: "8px 0", borderBottom: `1px solid ${C.border}22`, color: C.text }}>
              {li.description}
              {lineDisc > 0 && (
                <div style={{ fontSize: 10, color: C.green, marginTop: 2 }}>{`${li.discount?.name || "Discount"} · ${li.discount?.type === "%" ? `${li.discount.value}% off` : `$${li.discount?.value} off ea.`} · saves ${fmt(lineDisc)}`}</div>
              )}
            </td>
            <td data-label="Qty" style={{ fontSize: 12, fontWeight: 300, padding: "8px 0", borderBottom: `1px solid ${C.border}22`, color: C.muted }}>{hasQty ? li.qty : ""}</td>
            <td data-label="Rate" style={{ fontSize: 12, fontWeight: 300, padding: "8px 0", borderBottom: `1px solid ${C.border}22`, color: C.muted }}>{fmt(li.rate)}</td>
            <td data-label="Total" style={{ fontSize: 12, fontWeight: 300, padding: "8px 0", borderBottom: `1px solid ${C.border}22`, color: isNeg ? C.green : hasQty ? C.text : C.muted, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{hasQty ? fmt(liTotal) : "—"}</td>
          </tr>);
        })}</tbody>
      </table>
      {/* Totals — same math as the quote PDF (calcTotals), so the numbers here,
          on the document, and in the banner cannot disagree */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, marginTop: 8 }}>
        {totals.totalSavings > 0 && (<>
          <div style={{ fontSize: 11, color: C.muted, fontVariantNumeric: "tabular-nums" }}>{`Subtotal ${fmt(totals.grossSubtotal)}`}</div>
          {totals.lineDiscountTotal > 0 && (
            <div style={{ fontSize: 11, color: C.green, fontVariantNumeric: "tabular-nums" }}>{`Line discounts −${fmt(totals.lineDiscountTotal)}`}</div>
          )}
          {totals.quoteDiscountTotal > 0 && (
            <div style={{ fontSize: 11, color: C.green, fontVariantNumeric: "tabular-nums" }}>{`Quote discounts −${fmt(totals.quoteDiscountTotal)}`}</div>
          )}
        </>)}
        <div style={{ fontSize: 12, fontWeight: 400, color: C.text, fontVariantNumeric: "tabular-nums" }}>{`${docLabelShort} Total ${fmt(totals.total)}`}</div>
      </div>
    </>)}

    <div style={{ height: 1, background: C.border, margin: "16px 0" }} />

    {/* Billing milestones. A pro bono job has none: no invoice, nothing to
        collect. The toggles are hidden, not disabled, so nothing can be
        invoiced by accident; the flip back to standard lives here too. */}
    <div style={lbl}>Billing</div>
    {proBono ? (
      <div data-testid="jfin-pro-bono" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <ProBonoBadge />
        <span style={{ fontSize: 11, color: C.muted }}>No invoice. Nothing to collect. Value provided {fmt(givenValue)}.</span>
        {editing && (
          <button type="button" data-testid="jfin-pro-bono-off" style={{ ...btnS, padding: "4px 10px", fontSize: 10 }} onClick={() => flipProBono(false)}>Bill this job</button>
        )}
      </div>
    ) : (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <button data-testid="jfin-invoiced-toggle" onClick={() => {
        if (!editing) return;
        if (isInvoiced) { setConfirmUnInvoice(true); return; }
        toggleJobMilestone(job.id, "invoiced");
      }} style={{
        display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 10,
        fontSize: 10, fontFamily: fl, fontWeight: isInvoiced ? 500 : 400, textTransform: "uppercase", letterSpacing: "0.06em",
        background: isInvoiced ? `${invoicedColor}14` : "transparent",
        color: isInvoiced ? invoicedColor : C.muted,
        border: `1px solid ${isInvoiced ? invoicedColor + "40" : C.border}`,
        cursor: editing ? "pointer" : "default", transition: "all 0.15s ease",
      }}>
        {isInvoiced && "✓ "}Invoiced
      </button>
      {isInvoiced && job.invoiceDate && <span style={{ fontSize: 10, color: C.muted, fontVariantNumeric: "tabular-nums" }}>{fmtIsoDay(job.invoiceDate)}</span>}
      <button data-testid="jfin-paid-toggle" onClick={() => { if (editing) toggleJobMilestone(job.id, "paid"); }} style={{
        display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 10,
        fontSize: 10, fontFamily: fl, fontWeight: isPaid ? 500 : 400, textTransform: "uppercase", letterSpacing: "0.06em",
        background: isPaid ? `${paidColor}14` : "transparent",
        color: isPaid ? paidColor : C.muted,
        border: `1px solid ${isPaid ? paidColor + "40" : C.border}`,
        opacity: !isInvoiced ? 0.4 : 1,
        cursor: editing ? "pointer" : "default", transition: "all 0.15s ease",
      }}>
        {isPaid && "✓ "}Paid
      </button>
      {isPaid && job.paidDate && <span style={{ fontSize: 10, color: C.muted, fontVariantNumeric: "tabular-nums" }}>{fmtIsoDay(job.paidDate)}</span>}
      {/* Flip to pro bono: only while nothing has been billed. An invoiced or
          paid job has to be un-invoiced first, like an amendment. */}
      {editing && !isInvoiced && !isPaid && (
        <button type="button" data-testid="jfin-pro-bono-on" style={{ ...btnS, padding: "4px 10px", fontSize: 10, marginLeft: "auto" }} onClick={() => flipProBono(true)}>Make pro bono</button>
      )}
    </div>
    )}

    {/* Un-invoice confirmation */}
    {confirmUnInvoice && (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 6, marginTop: 10 }}>
        <span style={{ color: C.amber, fontSize: 14, flexShrink: 0 }}>{"⚠"}</span>
        <span style={{ fontSize: 11, color: C.text, flex: 1 }}>Un-invoicing will also clear the paid status. Continue?</span>
        <button style={{ ...btnS, padding: "4px 10px", fontSize: 10, color: C.amber, borderColor: C.amber + "50" }} onClick={() => { toggleJobMilestone(job.id, "invoiced"); setConfirmUnInvoice(false); }}>Yes, un-invoice</button>
        <button style={{ ...btnS, padding: "4px 10px", fontSize: 10 }} onClick={() => setConfirmUnInvoice(false)}>Cancel</button>
      </div>
    )}

    {/* Version History — only meaningful when a quote is linked */}
    {linkedQuote && (<>
    <div style={{ height: 1, background: C.border, margin: "16px 0" }} />
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
      <div style={lbl}>Version History</div>
      {linkedQuote && hasRestorableVersion && (
        <button
          type="button"
          style={{ ...btnS, padding: "4px 10px", fontSize: 10, color: C.amber, borderColor: C.amber + "50" }}
          onClick={() => onOpenVersions?.()}
        >Review restore</button>
      )}
    </div>
    {/* Current version — expands to its line items like the prior versions do */}
    <div onClick={() => setExpandedVersion(expandedVersion === "current" ? null : "current")} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}22`, cursor: lineItems.length > 0 ? "pointer" : "default", transition: "background 0.1s ease" }}>
      <span style={{ fontSize: 11, fontWeight: 400, color: C.text, fontFamily: fm, minWidth: 28 }}>v{visibleVersions.length + 1}</span>
      <span data-testid="jfin-version-current-date" style={{ fontSize: 11, fontWeight: 300, color: C.muted, flex: 1, marginLeft: 10 }}>{`since ${fmtIsoDay(currentSince)}`}</span>
      <span style={{ fontSize: 9, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.06em", padding: "2px 8px", borderRadius: 4, background: `${C.green}12`, color: C.green, marginLeft: 10 }}>Current</span>
      <span style={{ fontSize: 12, fontWeight: 400, color: C.text, fontVariantNumeric: "tabular-nums", marginLeft: 10 }}>{fmt(quoteTotal)}</span>
    </div>
    {expandedVersion === "current" && lineItems.length > 0 && (
      <div style={{ padding: "12px 0 8px 38px" }}>
        <table style={{ width: "100%" }}><tbody>
          {lineItems.map((li, li_i) => {
            // Net of the line's own discount (calcLineDiscount) so these rows
            // track the version total rather than a pre-discount qty*rate.
            const liT = (li.qty && li.qty > 0) ? Math.max(0, li.qty * (Number(li.rate) || 0) - calcLineDiscount(li)) : 0;
            return (<tr key={li_i}>
              <td style={{ fontSize: 11, fontWeight: 300, color: C.muted, padding: "3px 0" }}>{li.description}</td>
              <td style={{ fontSize: 11, fontWeight: 300, color: C.muted, padding: "3px 0", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{li.qty > 0 ? `${li.qty} x ${fmt(li.rate)} = ${fmt(liT)}` : "—"}</td>
            </tr>);
          })}
        </tbody></table>
      </div>
    )}
    {/* Previous versions (newest first) */}
    {[...visibleVersions].reverse().map((v, i) => {
      const vNum = visibleVersions.length - i;
      const vKey = `v${vNum}`;
      return (<div key={vKey}>
        <div onClick={() => setExpandedVersion(expandedVersion === vKey ? null : vKey)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}22`, cursor: "pointer", transition: "background 0.1s ease" }}>
          <span style={{ fontSize: 11, fontWeight: 400, color: C.text, fontFamily: fm, minWidth: 28 }}>v{vNum}</span>
          <span data-testid="jfin-version-archived-date" style={{ fontSize: 11, fontWeight: 300, color: C.muted, flex: 1, marginLeft: 10 }}>{`archived ${fmtIsoDay(v.timestamp)}`}</span>
          <span style={{ fontSize: 12, fontWeight: 400, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmt(v.total || 0)}</span>
        </div>
        {expandedVersion === vKey && v.lineItems?.length > 0 && (
          <div style={{ padding: "12px 0 8px 38px" }}>
            <table style={{ width: "100%" }}><tbody>
              {v.lineItems.map((li, li_i) => {
                const liT = (li.qty && li.qty > 0) ? Math.max(0, li.qty * (Number(li.rate) || 0) - calcLineDiscount(li)) : 0;
                return (<tr key={li_i}>
                  <td style={{ fontSize: 11, fontWeight: 300, color: C.muted, padding: "3px 0" }}>{li.description}</td>
                  <td style={{ fontSize: 11, fontWeight: 300, color: C.muted, padding: "3px 0", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{li.qty > 0 ? `${li.qty} x ${fmt(li.rate)} = ${fmt(liT)}` : "—"}</td>
                </tr>);
              })}
            </tbody></table>
          </div>
        )}
      </div>);
    })}
    </>)}

    {/* Related Quotes (generated from this job) */}
    {relatedQuotes.length > 0 && (<>
      <div style={{ height: 1, background: C.border, margin: "16px 0" }} />
      <div style={lbl}>Related Quotes</div>
      {relatedQuotes.map(rq => (
        <div key={rq.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 12px", background: C.surfaceAlt, borderRadius: 6, marginTop: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 300, color: C.text, flex: 1 }}>{rq.summary || rq.clientName || "Change order"}</div>
          <span style={{ fontSize: 11, fontWeight: 400, color: C.text, fontVariantNumeric: "tabular-nums" }}>{fmt(resolveQuoteRevenue(rq))}</span>
          <span onClick={() => openUnlessCancelled(() => loadQuote(rq), onOpenBuilder, onOpenBuilder)} style={{ fontSize: 10, fontFamily: fm, color: C.blue, cursor: "pointer" }}>{rq.id} {"→"}</span>
        </div>
      ))}
    </>)}

    <div style={{ height: 1, background: C.border, margin: "16px 0" }} />

    {/* Warning banner when invoiced */}
    {isInvoiced && (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 6, marginBottom: 14, fontSize: 11, color: C.text }}>
        <span style={{ color: C.amber, fontSize: 14, flexShrink: 0 }}>{"⚠"}</span>
        <span>This {docLabelShort.toLowerCase()} has been invoiced{isPaid ? " and paid" : ""}. To amend, un-invoice first.</span>
      </div>
    )}

    {confirmAmend && (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 6, marginBottom: 14 }}>
        <span style={{ color: C.amber, fontSize: 14, flexShrink: 0 }}>{"⚠"}</span>
        <span style={{ fontSize: 11, color: C.text, flex: 1 }}>Amending will pull the job's current schedule and location into a new {docLabelShort.toLowerCase()} version. The current {docLabelShort.toLowerCase()} is archived in Version History. Continue?</span>
        <button style={{ ...btnS, padding: "4px 10px", fontSize: 10, color: C.amber, borderColor: C.amber + "50" }} onClick={async () => {
          await openUnlessCancelled(() => amendQuote(linkedQuote, job), onOpenBuilder, onOpenBuilder);
          setConfirmAmend(false);
        }}>Yes, amend</button>
        <button style={{ ...btnS, padding: "4px 10px", fontSize: 10 }} onClick={() => setConfirmAmend(false)}>Cancel</button>
      </div>
    )}

    {/* Action buttons */}
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <button onClick={() => setShowPdf(true)} disabled={!linkedQuote} style={{
        ...btnS, padding: "6px 14px", fontSize: 11,
        opacity: !linkedQuote ? 0.5 : 1,
        cursor: !linkedQuote ? "default" : "pointer",
        display: "inline-flex", alignItems: "center", gap: 6,
      }}>
        &darr; {docLabelShort}
      </button>
      <button onClick={() => setConfirmAmend(true)} disabled={!linkedQuote || isInvoiced || confirmAmend} style={{
        ...btnS, padding: "6px 14px", fontSize: 11,
        color: C.amber, borderColor: `rgba(245,158,11,0.35)`,
        opacity: (!linkedQuote || isInvoiced || confirmAmend) ? 0.35 : 1,
        cursor: (!linkedQuote || isInvoiced || confirmAmend) ? "default" : "pointer",
        pointerEvents: (!linkedQuote || isInvoiced || confirmAmend) ? "none" : "auto",
        display: "inline-flex", alignItems: "center", gap: 6,
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        Amend {docLabelShort}
      </button>
      <button onClick={() => openUnlessCancelled(() => createChangeOrderQuote(job, linkedQuote), onOpenBuilder, onOpenBuilder)} style={{
        ...btnS, padding: "6px 14px", fontSize: 11,
        color: C.green, borderColor: `rgba(16,185,129,0.35)`,
        display: "inline-flex", alignItems: "center", gap: 6,
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
        Generate New {docLabelShort} from Job
      </button>
      {/* Divider: everything left of it acts on THIS job's billing; Duplicate is
          the one action that leaves the job behind — an independent copy for a
          new engagement (repeat client). No job linkage, so it stays enabled
          while invoiced. Approved mockup slice4-financials-duplicate, 2026-08-24. */}
      <span style={{ width: 1, alignSelf: "stretch", background: C.border, margin: "0 4px" }} />
      <button onClick={async () => {
        setDuplicating(true);
        try {
          await openUnlessCancelled(() => duplicateQuote(linkedQuote), onOpenBuilder, onOpenBuilder);
        } catch (err) {
          notify.error(`Duplicate failed: ${err?.message || err}`);
        } finally {
          setDuplicating(false);
        }
      }} disabled={!linkedQuote || duplicating} style={{
        ...btnS, padding: "6px 14px", fontSize: 11,
        opacity: (!linkedQuote || duplicating) ? 0.5 : 1,
        cursor: (!linkedQuote || duplicating) ? "default" : "pointer",
        display: "inline-flex", alignItems: "center", gap: 6,
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
        Duplicate as New {docLabelShort}
      </button>
    </div>

    {showPdf && linkedQuote && (
      <QuotePdfPreview quote={linkedQuote} notice={unsavedEditsNotice?.(linkedQuote.id) ? `${unsavedEditsNotice(linkedQuote.id)} This PDF shows the saved quote without them.` : ""} onClose={() => setShowPdf(false)} closeLabel="← Back" />
    )}
  </div>);
}

// Pro bono badge (2026-09-11). Same size and casing as the Current tag in the
// version list, muted rather than green: it marks a choice, not a milestone.
function ProBonoBadge() {
  return (
    <span data-testid="jfin-pro-bono-badge" style={{ fontSize: 9, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.06em", padding: "2px 8px", borderRadius: 4, background: `${C.muted}18`, color: C.text }}>Pro bono</span>
  );
}
